import { useState } from "react";
import API, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { LinkSimple, Copy, ArrowSquareOut, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

/* Branded sign-in link — also the client's portal URL prefix (/apc → /apc/jobs, /apc/invoices …).
   Main clients always have one; sub-clients may be given their own or share the main client's. */
export default function PortalLinkRow({ clientId, slug, ownSlug, inheritedFrom, active, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState("");
  const origin = window.location.origin;
  const loginUrl = slug ? `${origin}/login/${slug}` : "";
  const isSub = !!inheritedFrom;
  const shared = isSub && !ownSlug;

  const save = async (value) => {
    try {
      const { data } = await API.put(`/clients/${clientId}/branding`, { portal_slug: value });
      onSaved?.(data);
      setEdit(false);
      toast.success(value ? "Portal link updated" : `Now sharing ${inheritedFrom?.name || "the main client"}'s link`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const copy = () => { navigator.clipboard?.writeText(loginUrl); toast.success("Sign-in link copied"); };

  return (
    <div className="border-t border-border px-4 py-3 bg-muted/10" data-testid="client-branding-link-row">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <LinkSimple size={14} className="text-primary" />
        <p className="text-xs font-semibold">Branded sign-in link &amp; portal address</p>
        <span className="text-[10px] text-muted-foreground">— their sign-in page, and every portal page lives under it (e.g. <span className="font-mono">/{slug || "apc"}/jobs</span>).</span>
        {isSub && <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${shared ? "bg-muted text-muted-foreground" : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400"}`} data-testid="client-branding-slug-mode">{shared ? `Shared with ${inheritedFrom.name}` : "Own link"}</span>}
      </div>
      {!active && !isSub ? (
        <p className="text-[11px] text-muted-foreground" data-testid="client-branding-link-hint">Save a display name or logo first — the link is generated automatically.</p>
      ) : edit ? (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-mono text-muted-foreground">{origin}/login/</span>
          <Input value={draft} onChange={(e) => setDraft(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} className="h-8 text-xs font-mono w-56" placeholder={isSub ? "apc" : "wilson-property"} autoFocus data-testid="client-branding-slug-input" />
          <Button size="sm" onClick={() => save(draft.trim())} disabled={!draft.trim()} data-testid="client-branding-slug-save">Save</Button>
          {isSub && ownSlug && <Button size="sm" variant="ghost" onClick={() => save("")} data-testid="client-branding-slug-share">Share main client's link instead</Button>}
          <Button size="sm" variant="ghost" onClick={() => setEdit(false)} data-testid="client-branding-slug-cancel">Cancel</Button>
          <span className="text-[10px] text-muted-foreground">3-40 chars: lowercase letters, numbers, hyphens</span>
        </div>
      ) : (
        <div className="flex items-center gap-2 flex-wrap">
          <code className="text-xs bg-muted px-2 py-1 rounded font-mono break-all" data-testid="client-branding-login-url">{loginUrl || "Generating…"}</code>
          {loginUrl && <>
            <Button size="sm" variant="outline" onClick={copy} data-testid="client-branding-copy-link"><Copy size={13} className="mr-1" /> Copy</Button>
            <Button size="sm" variant="ghost" asChild><a href={loginUrl} target="_blank" rel="noreferrer" data-testid="client-branding-open-link"><ArrowSquareOut size={13} className="mr-1" /> Preview</a></Button>
          </>}
          <Button size="sm" variant="ghost" onClick={() => { setDraft(ownSlug || (isSub ? "" : slug)); setEdit(true); }} data-testid="client-branding-edit-slug"><PencilSimple size={13} className="mr-1" /> {shared ? "Give this client its own link" : "Customise"}</Button>
        </div>
      )}
      {shared && <p className="text-[11px] text-muted-foreground mt-1.5" data-testid="client-branding-slug-shared-note">Users of this sub-client currently sign in and browse at the main client's address. Give it its own short link (e.g. <span className="font-mono">/apc</span>) and the whole portal — jobs, invoices, requests — moves to <span className="font-mono">/apc/…</span> while keeping {inheritedFrom.name}'s look.</p>}
    </div>
  );
}
